/**
 * DisplaySettingsPage — Manage Customer-Facing 2nd Screen
 * Upload logo, idle images, configure branding, connection options
 */
import React, { useState, useEffect, useCallback } from 'react';
import { pwaUrl } from '@/utils/getBasePath';
import {
  Monitor, Wifi, Usb, Bluetooth, HdmiPort, Save, RefreshCw, Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ImageUpload from '@/components/shared/ImageUpload';
import { useOutletContext } from 'react-router-dom';
import { copyText } from '@/lib/copyText';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CONNECTION_METHODS = [
  { key: 'wifi', label: 'Same WiFi Network', icon: Wifi, status: 'Web Ready', supported: true, desc: 'Display opens a URL on same network' },
  { key: 'usb', label: 'USB Connection', icon: Usb, status: 'Flutter Only', supported: false, desc: 'Direct USB for Android displays' },
  { key: 'bluetooth', label: 'Bluetooth', icon: Bluetooth, status: 'Flutter Only', supported: false, desc: 'Pair via Bluetooth for nearby displays' },
  { key: 'hdmi', label: 'HDMI / Extended', icon: HdmiPort, status: 'Desktop App', supported: false, desc: 'Extended desktop to second monitor' },
];

const DEFAULT_MESSAGES = [
  'Welcome',
  'Thank you for dining with us',
  'Checkout will appear here when ready',
];

export default function DisplaySettingsPage() {
  const context = useOutletContext() || {};
  const token = context.token || localStorage.getItem('backoffice_token');
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    restaurant_name: '',
    tagline: '',
    accent_color: '#d4a053',
    logo_url: '',
    idle_image_url: '',
    welcome_messages: DEFAULT_MESSAGES,
    promo_title: '',
    promo_text: '',
    subtext: 'Please review and pay here when checkout begins',
  });
  // Display Settings belong to the restaurant being managed: the server keys
  // them by restaurant id and refuses any other key (403).
  const restaurantId = context.activeRestaurantId || context.restaurant?.id || null;
  const configKey = restaurantId ? String(restaurantId) : null;
  const authHeaders = (extra = {}) => ({
    ...extra,
    Authorization: `Bearer ${token}`,
    ...(restaurantId ? { 'X-Merchant-ID': String(restaurantId) } : {}),
  });

  const loadConfig = useCallback(async () => {
    if (!configKey) return;
    try {
      const res = await fetch(`${API}/api/display/config/${configKey}`);
      const data = await res.json();
      if (data.success && data.config && Object.keys(data.config).length > 0) {
        setConfig(prev => ({ ...prev, ...data.config }));
      }
    } catch { /* use defaults */ }
  }, [configKey]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const saveConfig = async () => {
    if (!configKey) { toast.error('No restaurant selected'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/display/config/${configKey}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(config),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success('Display settings saved');
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Display settings were not saved');
      }
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateMessage = (idx, val) => {
    const msgs = [...config.welcome_messages];
    msgs[idx] = val;
    setConfig(p => ({ ...p, welcome_messages: msgs }));
  };

  // display.chain29.com in production (app.chain29.com/display/... is the back office); T001 = the first register's
  // display channel; another register picks its own (T002, ...) in POS -> Settings -> Customer display
  const displayUrl = `${pwaUrl('display')}/t/T001?name=${encodeURIComponent(config.restaurant_name || 'Chain29')}`;

  return (
    <div className="space-y-6" data-testid="display-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Customer Display</h1>
          <p className="text-gray-500 text-sm">Configure the customer-facing 2nd screen</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(displayUrl, '_blank')}>
            <Eye className="w-4 h-4 mr-1" /> Preview
          </Button>
          <Button size="sm" onClick={saveConfig} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branding */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Branding</CardTitle>
            <CardDescription>Logo and name shown on the idle & checkout screens</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ImageUpload
              value={config.logo_url}
              onChange={(url) => setConfig(p => ({ ...p, logo_url: url }))}
              folder="display"
              label="Restaurant Logo"
            />
            <div>
              <Label>Restaurant Name</Label>
              <Input
                value={config.restaurant_name}
                onChange={(e) => setConfig(p => ({ ...p, restaurant_name: e.target.value }))}
                placeholder="My Restaurant"
              />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input
                value={config.tagline}
                onChange={(e) => setConfig(p => ({ ...p, tagline: e.target.value }))}
                placeholder="Fine dining since 2024"
              />
            </div>
            <div>
              <Label>Accent Color</Label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={config.accent_color}
                  onChange={(e) => setConfig(p => ({ ...p, accent_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg border cursor-pointer"
                />
                <Input
                  value={config.accent_color}
                  onChange={(e) => setConfig(p => ({ ...p, accent_color: e.target.value }))}
                  className="flex-1"
                  placeholder="#d4a053"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Idle Screen */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Idle Screen</CardTitle>
            <CardDescription>Background image and messages when idle</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ImageUpload
              value={config.idle_image_url}
              onChange={(url) => setConfig(p => ({ ...p, idle_image_url: url }))}
              folder="display"
              label="Idle Background Image"
            />
            <div>
              <Label>Welcome Messages (rotate)</Label>
              <div className="space-y-2 mt-1">
                {config.welcome_messages.map((msg, i) => (
                  <Input
                    key={i}
                    value={msg}
                    onChange={(e) => updateMessage(i, e.target.value)}
                    placeholder={`Message ${i + 1}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label>Subtext</Label>
              <Input
                value={config.subtext}
                onChange={(e) => setConfig(p => ({ ...p, subtext: e.target.value }))}
                placeholder="Please review and pay here when checkout begins"
              />
            </div>
          </CardContent>
        </Card>

        {/* Promo Card */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Promotional Card</CardTitle>
            <CardDescription>Optional card shown on idle screen</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={config.promo_title}
                onChange={(e) => setConfig(p => ({ ...p, promo_title: e.target.value }))}
                placeholder="Today's Special"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={config.promo_text}
                onChange={(e) => setConfig(p => ({ ...p, promo_text: e.target.value }))}
                placeholder="Wagyu Burger with truffle fries..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Connection Methods */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Connection Methods</CardTitle>
            <CardDescription>How to connect the 2nd screen display</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {CONNECTION_METHODS.map(({ key, label, icon: Icon, status, supported, desc }) => (
              <div
                key={key}
                className={`flex items-center gap-3 p-3 rounded-lg border ${supported ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-gray-50/50'}`}
                data-testid={`connection-${key}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${supported ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-gray-500">{desc}</p>
                </div>
                <Badge variant={supported ? 'default' : 'secondary'} className="text-xs shrink-0">
                  {status}
                </Badge>
              </div>
            ))}

            <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-sm font-medium text-blue-900">Display URL (first register, channel T001)</p>
              <p className="text-xs text-blue-700 mt-1 break-all font-mono">{displayUrl}</p>
              <p className="text-xs text-blue-700 mt-1">
                More than one register? Give each one its own channel in POS &rarr; Settings &rarr; Customer display;
                it shows the link for that register's display.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => copyText(displayUrl, 'URL copied')}
              >
                Copy URL
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
